import { motion } from 'framer-motion';
import { Bot, Camera, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { openWhatsApp } from '../../utils/whatsapp';

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Bot,
  Camera,
};

export default function DualModeSection() {
  const siteConfig = useSiteConfig();
  const { dualMode, contact } = siteConfig;
  const [ctaLabel, setCtaLabel] = useState(dualMode.ctaText);

  const handleCta = () => {
    setCtaLabel('⏳ 正在開啟 WhatsApp...');
    openWhatsApp(contact.whatsapp, dualMode.whatsappMessage);
  };

  return (
    <section id="dual-mode" className="py-20 md:py-28 bg-deep-black relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-neon-purple/10 rounded-full blur-3xl" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neon-purple/30 bg-neon-purple/5 text-neon-purple text-xs md:text-sm font-medium mb-6">
            <Sparkles size={14} />
            雙模式一站式製作
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {dualMode.sectionTitle}
          </h2>
          <p className="text-base md:text-lg text-neon-cyan-glow max-w-2xl mx-auto">
            {dualMode.subtitle}
          </p>
        </motion.div>

        {/* Two mode cards connected by a "+" */}
        <div className="flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-0 mb-14">
          {dualMode.modes.map((mode, index) => {
            const Icon = iconMap[mode.icon] || Bot;
            return (
              <div key={mode.id} className="flex items-center">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="neon-card rounded-2xl p-8 w-full md:w-72 text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20 flex items-center justify-center border border-neon-cyan/30">
                    <Icon size={30} className="text-neon-cyan" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{mode.title}</h3>
                  <p className="text-xs text-neon-gold mb-4 font-medium">{mode.tagline}</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{mode.description}</p>
                </motion.div>
                {index < dualMode.modes.length - 1 && (
                  <div className="hidden md:flex items-center justify-center w-16">
                    <span className="text-3xl font-black text-neon-gold">+</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Mission statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="neon-card rounded-3xl p-8 md:p-12 text-center max-w-3xl mx-auto"
          style={{ borderColor: 'rgba(212,168,83,0.3)' }}
        >
          <p className="text-neon-gold text-sm font-semibold tracking-widest uppercase mb-4">
            {dualMode.missionLabel}
          </p>
          <p className="text-gray-200 text-base md:text-lg leading-relaxed mb-8">
            {dualMode.missionText}
          </p>
          <button
            onClick={handleCta}
            className="inline-block px-8 py-4 bg-gradient-to-r from-neon-cyan via-neon-blue to-neon-purple text-deep-navy font-bold rounded-full transition-all duration-300 hover:brightness-110 hover:-translate-y-0.5"
          >
            {ctaLabel}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
